import type { GetMessagesQueryVariables } from '#src/graphql/generated';

import { NetworkStatus } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { MessageList } from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { DASHBOARD_EXCLUDED_ASSOCIATION_CONTEXT_NAMES } from '#src/constants/dashboard-message-filters';
import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useScrollMessageListToEnd } from '#src/hooks/use-scroll-message-list-to-end';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import { usePageFooter } from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import { resolveFocusParamToRootId } from '#src/utils/message-thread-focus';

type DashboardMessagesScope = 'filtered' | 'all';

export function Dashboard() {
  const { footerRef } = usePageFooter();
  const user = useCurrentUser();

  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  const [messagesScope, setMessagesScope] =
    useState<DashboardMessagesScope>('filtered');

  const getMessagesVariables = useMemo<GetMessagesQueryVariables>(
    () =>
      messagesScope === 'filtered'
        ? {
            input: {
              excludeContextNames: [
                ...DASHBOARD_EXCLUDED_ASSOCIATION_CONTEXT_NAMES,
              ],
            },
          }
        : {},
    [messagesScope],
  );

  const [messageText, setMessageText] = useState('');

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);

  const {
    data: messagesData,
    error: messagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: getMessagesVariables,
    fetchPolicy: 'cache-and-network',
  });

  /**
   * `useQuery` does not expose a dedicated "initial only" flag; `networkStatus.loading`
   * is only true before the first result. Refetches (e.g. after AI actions or delete)
   * use `refetch` status and do not clear `messagesData`, so the list stays visible.
   * New messages from send are merged via the mutation `update` (no refetch).
   */
  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && messagesData == null;

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (isMessagesInitialLoad) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    isMessagesInitialLoad,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useScrollMessageListToEnd(messagesEndRef, displayMessages);

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    const hadNoThreadFocus = !focusedRootId;
    try {
      const { data } = await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: focusedRootId ?? undefined,
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData?.createMessage) {
            mergeCreateMessageIntoGetMessagesCache(
              cache,
              mutationData.createMessage,
              getMessagesVariables,
            );
          }
        },
      });
      setMessageText('');
      if (hadNoThreadFocus && data?.createMessage?.id) {
        expandThread(data.createMessage.id);
      }
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const footerContent = user.email ? (
    <VStack gap={2} align="stretch" width="100%">
      {focusedRootId && (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying in thread</Text>
          <Button size="xs" variant="ghost" onClick={() => collapseThread()}>
            Cancel
          </Button>
        </HStack>
      )}
      <ChatComposerRow
        value={messageText}
        onChange={setMessageText}
        onSubmit={handleSendMessage}
        placeholder={focusedRootId ? 'Type a reply…' : 'Type a message…'}
        loading={isSendingMessage}
        autoGrow
      />
    </VStack>
  ) : null;

  return (
    <Box p={4}>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <HStack justify="flex-end" align="center" gap={2} mb={2}>
        <Text fontSize="sm" color="fg.muted">
          Messages
        </Text>
        <ButtonGroup size="xs" variant="outline" attached>
          <Button
            variant={messagesScope === 'filtered' ? 'solid' : 'outline'}
            onClick={() => setMessagesScope('filtered')}
          >
            Chat
          </Button>
          <Button
            variant={messagesScope === 'all' ? 'solid' : 'outline'}
            onClick={() => setMessagesScope('all')}
          >
            All
          </Button>
        </ButtonGroup>
      </HStack>

      <MessageList
        messages={displayMessages}
        loading={isMessagesInitialLoad}
        error={messagesError ?? undefined}
        emptyMessage="No messages found."
        focusedRootId={focusedRootId}
        onExpandThread={expandThread}
        messagesEndRef={messagesEndRef}
        alignMessagesToBottom
        onMessageDeleted={() => void refetchMessages()}
      />
    </Box>
  );
}
